import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Linkedin, ArrowUp, FileText, Send } from 'lucide-react';
import { firmInfo, globalRequests } from '../data/mock';
import { RequestDialog } from './RequestDialog';

export const Footer = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});
  const [activeContext, setActiveContext] = React.useState("");

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRequest = (type) => {
    const config = globalRequests[type];
    setActiveConfig(config);
    setActiveContext(config.title);
    setIsRequestOpen(true);
  };

  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/practice-areas', label: 'Practice Areas' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/thought-leadership', label: 'Exclusive Insights' }
  ];

  return (
    <footer className="relative bg-black border-t border-gold/20">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context={activeContext}
      />
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <img
              src={firmInfo.logo}
              alt={firmInfo.name}
              className="h-10 w-auto mb-4"
            />
            <p className="text-gray-400 text-base font-medium leading-relaxed max-w-md mb-4">
              {firmInfo.tagline}
            </p>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md">
              {firmInfo.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 inline-flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-gold transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gold font-semibold text-sm uppercase tracking-wider mb-4">
              Get In Touch
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${firmInfo.email}`}
                  className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 flex items-center group"
                >
                  <Mail size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  {firmInfo.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${firmInfo.phone}`}
                  className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 flex items-center group"
                >
                  <Phone size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  {firmInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={firmInfo.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 flex items-center group"
                >
                  <Linkedin size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  LinkedIn
                </a>
              </li>
              <li className="pt-2">
                <button
                  onClick={() => handleRequest('firmProfile')}
                  className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 flex items-center group"
                >
                  <FileText size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  Get Firm Profile
                </button>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-400 hover:text-gold text-sm transition-colors duration-300 flex items-center group pt-2"
                >
                  <span className="w-0 group-hover:w-2 h-px bg-gold transition-all duration-300 mr-0 group-hover:mr-2"></span>
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gold/10 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} {firmInfo.name}. All rights reserved.
          </p>

          <div className="flex items-center space-x-6">
            <Link to="/" className="text-gray-500 hover:text-gold text-xs transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/" className="text-gray-500 hover:text-gold text-xs transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 p-3 bg-gold hover:bg-gold-light text-black rounded-full shadow-lg shadow-gold/30 hover:shadow-gold/50 transform hover:scale-110 transition-all duration-300 z-40"
        aria-label="Scroll to top"
      >
        <ArrowUp size={20} />
      </button>
    </footer>
  );
};
